import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Boxes, ShoppingCart, Wallet, Users, FileText, ShieldCheck,
  Check, ArrowRight, Zap, Globe, Sparkles, TrendingUp, Menu, X
} from 'lucide-react';

const GOLD = '#F59E0B';

const useCounter = (target, run, duration = 1800) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let start = null;
    let raf;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      setVal(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [run, target, duration]);
  return val;
};

const CAPS = [
  { icon: Boxes, title: 'Inventario Inteligente', desc: 'Multialmacén, categorías con color, carga masiva desde Excel y control de Precio B (Tasa BCV/USDT).' },
  { icon: ShoppingCart, title: 'Ventas & POS Ultrarrápido', desc: 'Facturación en segundos con buscador instantáneo de productos y clientes, presupuestos y notas de entrega.' },
  { icon: Wallet, title: 'Finanzas Pro', desc: 'Cajas, bancos, cuentas por cobrar y pagar, cierres de ejercicio y flujo de caja consolidado.' },
  { icon: Users, title: 'Nómina & Personal', desc: 'Pagos a personal con recibo, perfiles de empleado y trazabilidad contable completa.' },
  { icon: FileText, title: 'Reportes Ejecutivos', desc: 'Exporta a PDF y Excel con tu marca, y comparte por WhatsApp con un solo toque.' },
  { icon: ShieldCheck, title: 'Respaldos Automáticos', desc: 'Copias diarias programadas en la nube, con aislamiento estricto de datos por empresa.' },
];

const PLANS = [
  { id: 'pyme', name: 'Pyme', tagline: 'Para emprendedores', monthly: 29, features: ['1 empresa', 'Inventario y ventas', 'Reportes PDF/Excel', 'Soporte por correo'], highlight: false },
  { id: 'enterprise', name: 'Empresarial', tagline: 'Multiempresa', monthly: 79, features: ['Hasta 5 empresas', 'Finanzas Pro + Nómina', 'Tienda web por empresa', 'Respaldos automáticos', 'Soporte prioritario'], highlight: true },
  { id: 'elite', name: 'Corporativo Elite', tagline: 'A medida', monthly: 149, features: ['Empresas ilimitadas', 'Dominios personalizados', 'Migración de datos gratis', 'Capacitación VIP', 'Soporte dedicado 24/7'], highlight: false },
];

const useCountdown = (hoursFromNow = 72) => {
  const target = useRef(Date.now() + hoursFromNow * 3600 * 1000);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const diff = Math.max(0, target.current - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s };
};

export default function SiteSalesPRO() {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(true);
  const [heroIn, setHeroIn] = useState(false);
  const [statsRun, setStatsRun] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const statsRef = useRef(null);
  const cd = useCountdown(72);

  const companies = useCounter(1200, statsRun);
  const txs = useCounter(875000, statsRun);
  const eff = useCounter(99, statsRun);

  useEffect(() => { const t = setTimeout(() => setHeroIn(true), 100); return () => clearTimeout(t); }, []);
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((e) => { if (e[0].isIntersecting) { setStatsRun(true); obs.disconnect(); } }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const price = (p) => annual ? Math.round(p.monthly * 12 * 0.8) : p.monthly;
  const goLogin = () => navigate('/login');
  const nav = (id) => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };

  return (
    <div className="min-h-screen bg-[#030A08] text-white overflow-x-hidden font-body" data-testid="salespro-landing">
      <style>{`
        @keyframes spro-spin { from { transform: rotateY(0deg); } to { transform: rotateY(360deg); } }
        @keyframes spro-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-14px); } }
        @keyframes spro-pulse-glow { 0%,100% { opacity: .5; } 50% { opacity: 1; } }
        @keyframes spro-rise { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spro-beam { 0% { transform: translateX(-120%); } 100% { transform: translateX(120%); } }
        .spro-rise { animation: spro-rise .9s cubic-bezier(.2,.7,.2,1) both; }
        .spro-3d { transform-style: preserve-3d; animation: spro-spin 14s linear infinite, spro-float 5s ease-in-out infinite; }
        .spro-circuit { background-image: radial-gradient(#00BFA5 1px, transparent 1px); background-size: 26px 26px; }
        .spro-glass { backdrop-filter: blur(18px); background: rgba(4,30,24,.6); border: 1px solid rgba(0,191,165,.2); }
        .spro-beam::after { content:''; position:absolute; top:0; left:0; height:100%; width:40%; background:linear-gradient(90deg,transparent,rgba(245,158,11,.25),transparent); animation: spro-beam 3.2s ease-in-out infinite; }
      `}</style>

      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 spro-glass" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => nav('hero')} className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/brand/salespro_logo.jpg" alt="SalesPRO" className="h-10 w-10 rounded-lg object-cover ring-1 ring-[#F59E0B]/40 shadow-[0_0_18px_rgba(245,158,11,.4)]" />
            <span className="font-heading text-xl font-extrabold tracking-tight">Sales<span style={{ color: GOLD }}>PRO</span></span>
          </button>
          <nav className="hidden lg:flex items-center gap-7 text-sm text-emerald-100/80">
            {[['Capacidades', 'capabilities'], ['Multiempresa', 'multi'], ['Planes', 'pricing'], ['Promociones', 'promos'], ['Testimonios', 'testimonials']].map(([l, id]) => (
              <button key={id} onClick={() => nav(id)} className="hover:text-white transition-colors" data-testid={`nav-link-${id}`}>{l}</button>
            ))}
          </nav>
          <div className="hidden sm:flex items-center gap-3">
            <button onClick={() => nav('cta')} className="text-sm text-emerald-100/80 hover:text-white transition-colors" data-testid="nav-demo">Solicitar Demo</button>
            <button onClick={goLogin} className="group inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-[#022C22] bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] hover:shadow-[0_0_30px_rgba(245,158,11,.6)] transition-shadow" data-testid="nav-login">
              Iniciar Sesión <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
          <button className="lg:hidden text-white" onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-mobile-toggle">{menuOpen ? <X /> : <Menu />}</button>
        </div>
        {menuOpen && (
          <div className="lg:hidden spro-glass border-t border-[#00BFA5]/20 px-4 py-4 space-y-2" data-testid="nav-mobile-menu">
            {[['Capacidades', 'capabilities'], ['Multiempresa', 'multi'], ['Planes', 'pricing'], ['Promociones', 'promos'], ['Testimonios', 'testimonials']].map(([l, id]) => (
              <button key={id} onClick={() => nav(id)} className="block w-full text-left py-2 text-emerald-100/80">{l}</button>
            ))}
            <button onClick={goLogin} className="w-full mt-2 px-4 py-2 rounded-full font-semibold text-[#022C22] bg-gradient-to-r from-[#FBBF24] to-[#F59E0B]" data-testid="nav-login-mobile">Iniciar Sesión</button>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="hero" className="relative pt-32 pb-24 px-4 sm:px-6" data-testid="hero-section">
        <div className="absolute inset-0 spro-circuit opacity-[0.12]" />
        <div className="absolute -top-20 left-1/4 w-[420px] h-[420px] rounded-full bg-[#00BFA5]/20 blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[360px] h-[360px] rounded-full bg-[#F59E0B]/15 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className={heroIn ? 'spro-rise' : 'opacity-0'}>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-[0.2em] text-amber-400 border border-[#F59E0B]/30 bg-[#F59E0B]/5" data-testid="hero-badge">
              <Sparkles size={13} /> Plataforma Oficial · www.salespr.win
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Controla <span className="bg-gradient-to-r from-[#00BFA5] via-[#5EEAD4] to-[#FBBF24] bg-clip-text text-transparent">varias empresas</span> desde una sola plataforma
            </h1>
            <p className="mt-5 text-base sm:text-lg text-emerald-100/80 max-w-xl leading-relaxed">
              Ventas, inventario multialmacén, finanzas, nómina y reportes — con tienda web propia para cada empresa. Potencia de nivel corporativo, estética premium.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button onClick={goLogin} className="group inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-[#022C22] bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] hover:shadow-[0_0_36px_rgba(245,158,11,.6)] transition-shadow" data-testid="hero-cta-login">
                Iniciar Sesión <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => nav('cta')} className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border border-[#00BFA5]/40 text-white hover:bg-[#00BFA5]/10 transition-colors" data-testid="hero-cta-demo">
                <Zap size={18} className="text-[#00BFA5]" /> Solicitar Demo
              </button>
            </div>
          </div>
          {/* 3D rotating logo cube */}
          <div className="flex justify-center lg:justify-end" style={{ perspective: '1000px' }}>
            <div className="relative">
              <div className="absolute inset-0 rounded-3xl bg-[#00BFA5]/30 blur-3xl" />
              <div className="spro-3d relative w-56 h-56 sm:w-72 sm:h-72 rounded-3xl spro-glass border-2 border-[#F59E0B]/40 flex items-center justify-center shadow-[0_0_60px_rgba(0,191,165,.4)]" data-testid="hero-3d-logo">
                <img src="/brand/salespro_logo.jpg" alt="SalesPRO" className="w-40 h-40 sm:w-52 sm:h-52 rounded-2xl object-cover" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div ref={statsRef} className="relative max-w-5xl mx-auto mt-20 grid grid-cols-3 gap-4 sm:gap-8" data-testid="hero-stats">
          {[[companies, '+', 'Empresas activas'], [txs, '+', 'Transacciones procesadas'], [eff, '%', 'Efectividad operativa']].map(([v, suf, label], i) => (
            <div key={i} className="text-center spro-glass rounded-2xl py-5 px-2">
              <div className="font-heading text-2xl sm:text-4xl font-extrabold bg-gradient-to-r from-[#00BFA5] to-[#FBBF24] bg-clip-text text-transparent">{v.toLocaleString()}{suf}</div>
              <div className="mt-1 text-xs sm:text-sm text-emerald-100/70">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CAPABILITIES BENTO */}
      <section id="capabilities" className="relative py-24 px-4 sm:px-6" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">Capacidades</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold">Todo tu negocio, en un solo lugar</h2>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPS.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className="group spro-glass rounded-2xl p-6 hover:border-[#F59E0B]/50 hover:-translate-y-1 transition-all duration-300" data-testid={`cap-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00BFA5]/20 to-[#F59E0B]/10 flex items-center justify-center border border-[#00BFA5]/30 group-hover:shadow-[0_0_24px_rgba(0,191,165,.4)] transition-shadow">
                    <Icon className="text-[#00BFA5]" size={24} />
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-semibold">{c.title}</h3>
                  <p className="mt-2 text-sm text-emerald-100/70 leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MULTI-COMPANY */}
      <section id="multi" className="relative py-24 px-4 sm:px-6" data-testid="multi-section">
        <div className="absolute inset-0 spro-circuit opacity-[0.08]" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">Multiempresa</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold">Una plataforma. Todas tus empresas.</h2>
            <p className="mt-4 text-emerald-100/80 leading-relaxed">Cambia entre empresas al instante con aislamiento total de datos y consolidación de reportes corporativos. Cada empresa puede tener su propia <span className="text-[#FBBF24] font-semibold">tienda web independiente</span>.</p>
            <ul className="mt-6 space-y-3">
              {['Aislamiento estricto de datos por empresa', 'Reportes corporativos consolidados', 'Tienda web premium por empresa', 'Permisos por rol (Master, Admin, Vendedor)'].map((t, i) => (
                <li key={i} className="flex items-center gap-3 text-sm"><Check size={18} className="text-[#00BFA5] shrink-0" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="grid gap-4">
            {['Empresa Norte S.A.', 'Empresa Sur S.R.L.', 'Tienda Web Multiempresa'].map((n, i) => (
              <div key={i} className="spro-glass rounded-2xl p-5 flex items-center gap-4 hover:translate-x-1 transition-transform" data-testid={`multi-company-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[#00BFA5]/15 border border-[#00BFA5]/30 flex items-center justify-center">
                  {i === 2 ? <Globe className="text-[#FBBF24]" /> : <Building2 className="text-[#00BFA5]" />}
                </div>
                <div className="flex-1"><p className="font-semibold">{n}</p><p className="text-xs text-emerald-100/60">Activa · Datos aislados</p></div>
                <TrendingUp size={18} className="text-[#00BFA5]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="relative py-24 px-4 sm:px-6" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">Planes y Tarifas</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold">Elige tu plan</h2>
            <div className="mt-6 inline-flex items-center gap-1 p-1 rounded-full spro-glass" data-testid="pricing-toggle">
              <button onClick={() => setAnnual(false)} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${!annual ? 'bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-[#022C22]' : 'text-emerald-100/70'}`} data-testid="pricing-monthly">Mensual</button>
              <button onClick={() => setAnnual(true)} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${annual ? 'bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-[#022C22]' : 'text-emerald-100/70'}`} data-testid="pricing-annual">Anual <span className="text-[10px]">-20%</span></button>
            </div>
          </div>
          <div className="mt-14 grid lg:grid-cols-3 gap-6 items-stretch">
            {PLANS.map((p) => (
              <div key={p.id} className={`relative rounded-3xl p-7 flex flex-col ${p.highlight ? 'spro-beam border-2 border-[#F59E0B]/50 bg-[#022C22]/80' : 'spro-glass'}`} data-testid={`pricing-plan-${p.id}`}>
                {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-[#022C22] bg-gradient-to-r from-[#FBBF24] to-[#F59E0B]">MÁS POPULAR</span>}
                <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                <p className="text-sm text-emerald-100/60">{p.tagline}</p>
                <div className="mt-5 flex items-end gap-1">
                  <span className="font-heading text-4xl font-extrabold">${price(p)}</span>
                  <span className="text-emerald-100/60 text-sm mb-1">/{annual ? 'año' : 'mes'}</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm text-emerald-100/80"><Check size={16} className="text-[#00BFA5] mt-0.5 shrink-0" /> {f}</li>)}
                </ul>
                <button onClick={goLogin} className={`mt-7 w-full py-3 rounded-full font-bold transition-shadow ${p.highlight ? 'text-[#022C22] bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] hover:shadow-[0_0_30px_rgba(245,158,11,.6)]' : 'border border-[#00BFA5]/40 hover:bg-[#00BFA5]/10'}`} data-testid={`pricing-cta-${p.id}`}>
                  Contratar
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMOS */}
      <section id="promos" className="relative py-20 px-4 sm:px-6" data-testid="promos-section">
        <div className="max-w-5xl mx-auto rounded-3xl spro-beam border border-[#F59E0B]/30 bg-gradient-to-br from-[#022C22] to-[#004D40] p-8 sm:p-12 text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">Promoción de Lanzamiento</span>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold">Migración gratis + Capacitación VIP</h2>
          <p className="mt-2 text-emerald-100/80">Contrata el Plan Empresarial y llévate migración de datos y dominio personalizado sin costo.</p>
          <div className="mt-6 flex justify-center gap-3" data-testid="promo-countdown">
            {[['Días', cd.d], ['Horas', cd.h], ['Min', cd.m], ['Seg', cd.s]].map(([l, v], i) => (
              <div key={i} className="w-16 sm:w-20 spro-glass rounded-xl py-3">
                <div className="font-heading text-2xl sm:text-3xl font-extrabold text-[#FBBF24]">{String(v).padStart(2, '0')}</div>
                <div className="text-[10px] uppercase tracking-wider text-emerald-100/60">{l}</div>
              </div>
            ))}
          </div>
          <button onClick={goLogin} className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-[#022C22] bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] hover:shadow-[0_0_30px_rgba(245,158,11,.6)] transition-shadow" data-testid="promo-cta">
            Aprovechar ahora <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="relative py-24 px-4 sm:px-6" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">Testimonios</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold">Confianza de nivel corporativo</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { n: 'Carlos M.', r: 'Director General', t: 'Pasamos de 3 hojas de cálculo a una sola plataforma. El control multiempresa es brutal.' },
              { n: 'Andrea R.', r: 'Gerente de Operaciones', t: 'La facturación es instantánea y los reportes por WhatsApp nos ahorran horas cada semana.' },
              { n: 'Luis F.', r: 'CFO', t: 'Finanzas Pro nos dio visibilidad real del flujo de caja consolidado. Impecable.' },
            ].map((x, i) => (
              <div key={i} className="spro-glass rounded-2xl p-6" data-testid={`testimonial-${i}`}>
                <p className="text-emerald-100/85 leading-relaxed">“{x.t}”</p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00BFA5] to-[#F59E0B]" />
                  <div><p className="font-semibold text-sm">{x.n}</p><p className="text-xs text-emerald-100/60">{x.r}</p></div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            {[['99.99%', 'Uptime'], ['GMT-4', 'Zona horaria'], ['256-bit', 'Cifrado'], ['24/7', 'Soporte Elite']].map(([v, l], i) => (
              <div key={i} className="spro-glass rounded-xl py-4"><div className="font-heading text-xl font-bold text-[#FBBF24]">{v}</div><div className="text-xs text-emerald-100/60">{l}</div></div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="cta" className="relative py-24 px-4 sm:px-6" data-testid="cta-section">
        <div className="absolute inset-0 spro-circuit opacity-[0.1]" />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold">Lleva tu operación al <span className="bg-gradient-to-r from-[#00BFA5] to-[#FBBF24] bg-clip-text text-transparent">siguiente nivel</span></h2>
          <p className="mt-4 text-emerald-100/80">Comienza hoy con SalesPRO y unifica todas tus empresas en una experiencia premium.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button onClick={goLogin} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-[#022C22] bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] hover:shadow-[0_0_36px_rgba(245,158,11,.6)] transition-shadow" data-testid="cta-login">
              Iniciar Sesión <ArrowRight size={18} />
            </button>
            <a href="https://www.7-fstudy.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold border border-[#00BFA5]/40 hover:bg-[#00BFA5]/10 transition-colors" data-testid="cta-contact">
              Solicitar Demo
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative border-t border-[#00BFA5]/15 bg-[#020806] px-4 sm:px-6 py-12" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2">
              <img src="/brand/salespro_logo.jpg" alt="SalesPRO" className="h-9 w-9 rounded-lg object-cover" />
              <span className="font-heading text-lg font-extrabold">Sales<span style={{ color: GOLD }}>PRO</span></span>
            </div>
            <p className="mt-3 text-sm text-emerald-100/60 max-w-xs">Plataforma integral de gestión de ventas, inventario y finanzas multiempresa.</p>
          </div>
          <div className="text-sm text-emerald-100/70 space-y-2">
            <p className="font-semibold text-white">Enlaces</p>
            <button onClick={() => nav('pricing')} className="block hover:text-white">Planes</button>
            <button onClick={() => nav('capabilities')} className="block hover:text-white">Capacidades</button>
            <a href="https://www.7-fstudy.com" target="_blank" rel="noreferrer" className="block hover:text-white">Soporte</a>
          </div>
          <div className="text-sm text-emerald-100/70 space-y-2">
            <p className="font-semibold text-white">Legal</p>
            <p>Política de Privacidad</p>
            <p>Términos de Servicio</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-10 pt-6 border-t border-[#00BFA5]/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-emerald-100/50">
          <p>© {new Date().getFullYear()} SalesPRO — Todos los derechos reservados · www.salespr.win</p>
          <p>Desarrollado y diseñado por <a href="https://www.7-fstudy.com" target="_blank" rel="noreferrer" className="font-semibold text-[#FBBF24] hover:underline">www.7-fstudy.com</a></p>
        </div>
      </footer>
    </div>
  );
}
